// components/admin/MembersModal.js - Modern UI/UX Version
import React, { useState, useEffect, useCallback } from 'react';
import {
  XMarkIcon,
  UserPlusIcon,
  MagnifyingGlassIcon,
  UserMinusIcon,
  ShieldCheckIcon,
  UserIcon,
  EnvelopeIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';
import {
  UserGroupIcon as UserGroupIconSolid
} from '@heroicons/react/24/solid';
import axiosInstance from '../api/axiosInstance';
import logger from '../../utils/logger';

const MembersModal = ({ open, onClose, team, onSubmit }) => {
  const [members, setMembers] = useState(team?.members || []);
  const [loading, setLoading] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [selectedRole, setSelectedRole] = useState('member');
  const [showAddSection, setShowAddSection] = useState(false);

  useEffect(() => {
    if (team?.members) {
      setMembers(team.members);
    }
  }, [team]);

  const fetchAvailableUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/business/users');

      const existingMemberIds = members.map(m => m.userId);
      const availableUsers = response.data.users.filter(
        user => !existingMemberIds.includes(user.id)
      );

      setAvailableUsers(availableUsers);
    } catch (error) {
      logger.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  }, [members]);

  useEffect(() => {
    if (open) {
      fetchAvailableUsers();
      setShowAddSection(false);
      setSelectedUsers(new Set());
      setSearchQuery('');
    }
  }, [open, fetchAvailableUsers]);

  const handleToggleUser = (userId) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  const handleAddMembers = () => {
    const usersToAdd = availableUsers.filter(user => selectedUsers.has(user.id));
    const newMembers = [
      ...members,
      ...usersToAdd.map(user => ({
        userId: user.id,
        role: selectedRole,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        department: user.department,
        status: 'active'
      }))
    ];
    setMembers(newMembers);
    setSelectedUsers(new Set());
    setShowAddSection(false);
    setSearchQuery('');
  };

  const handleRemoveMember = (userId) => {
    const newMembers = members.filter(member => member.userId !== userId);
    setMembers(newMembers);
  };

  const handleUpdateRole = (userId, newRole) => {
    const newMembers = members.map(member =>
      member.userId === userId ? { ...member, role: newRole } : member
    );
    setMembers(newMembers);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await onSubmit({ members });
      onClose();
    } catch (error) {
      logger.error('Error updating members:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredAvailableUsers = availableUsers.filter(user => {
    const searchLower = searchQuery.toLowerCase();
    return (
      user.firstName?.toLowerCase().includes(searchLower) ||
      user.lastName?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower) ||
      user.department?.toLowerCase().includes(searchLower)
    );
  });

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
                <UserGroupIconSolid className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Manage Team Members
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {team?.name}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-[#222222] transition-colors"
            >
              <XMarkIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Stats Bar */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                    <UserGroupIconSolid className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Current Members</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{members.length}</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-xl p-4 border border-green-200 dark:border-green-800">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                    <UserPlusIcon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Available to Add</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{availableUsers.length}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Add Members Section */}
            {!showAddSection ? (
              <button
                onClick={() => setShowAddSection(true)}
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors shadow-sm hover:shadow-md"
              >
                <UserPlusIcon className="w-5 h-5" />
                <span>Add New Members</span>
              </button>
            ) : (
              <div className="bg-gray-50 dark:bg-[#222222] rounded-xl p-4 space-y-4 border border-gray-200 dark:border-gray-800">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Add New Members</h3>
                  <button
                    onClick={() => {
                      setShowAddSection(false);
                      setSelectedUsers(new Set());
                      setSearchQuery('');
                    }}
                    className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    Cancel
                  </button>
                </div>

                {/* Search and Role Selection */}
                <div className="flex gap-3">
                  <div className="flex-1 relative">
                    <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search users..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  <select
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value)}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                {/* Available Users List */}
                <div className="max-h-60 overflow-y-auto space-y-2">
                  {filteredAvailableUsers.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <UserIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No users available to add</p>
                    </div>
                  ) : (
                    filteredAvailableUsers.map((user) => (
                      <div
                        key={user.id}
                        onClick={() => handleToggleUser(user.id)}
                        className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-all ${
                          selectedUsers.has(user.id)
                            ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-500'
                            : 'bg-white dark:bg-[#2a2a2a] border-gray-200 dark:border-gray-700 hover:border-orange-300'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          selectedUsers.has(user.id)
                            ? 'bg-orange-500 border-orange-500'
                            : 'border-gray-300 dark:border-gray-700'
                        }`}>
                          {selectedUsers.has(user.id) && (
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-medium">
                          {user.firstName?.[0]}{user.lastName?.[0]}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {user.firstName} {user.lastName}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.email}</p>
                        </div>
                        {user.department && (
                          <span className="px-2 py-1 bg-gray-100 dark:bg-[#2a2a2a] text-gray-700 dark:text-gray-300 text-xs rounded-md">
                            {user.department}
                          </span>
                        )}
                      </div>
                    ))
                  )}
                </div>

                {/* Add Button */}
                {selectedUsers.size > 0 && (
                  <button
                    onClick={handleAddMembers}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors"
                  >
                    <UserPlusIcon className="w-5 h-5" />
                    <span>Add {selectedUsers.size} {selectedUsers.size === 1 ? 'Member' : 'Members'}</span>
                  </button>
                )}
              </div>
            )}

            {/* Current Members List */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                Current Members ({members.length})
              </h3>

              {members.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 dark:bg-[#222222] rounded-xl border border-gray-200 dark:border-gray-800">
                  <UserGroupIconSolid className="w-16 h-16 mx-auto mb-3 text-gray-400" />
                  <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">No members yet</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Add members to get started</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {members.map((member) => (
                    <div
                      key={member.userId}
                      className="flex items-center space-x-3 p-4 bg-white dark:bg-[#222222] rounded-lg border border-gray-200 dark:border-gray-800 hover:shadow-md transition-shadow"
                    >
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center text-white font-medium">
                        {member.firstName?.[0]}{member.lastName?.[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {member.firstName} {member.lastName}
                          </p>
                          {member.department && (
                            <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs rounded-md flex items-center space-x-1">
                              <BuildingOfficeIcon className="w-3 h-3" />
                              <span>{member.department}</span>
                            </span>
                          )}
                        </div>
                        <div className="flex items-center space-x-1 text-xs text-gray-500 dark:text-gray-400">
                          <EnvelopeIcon className="w-3 h-3" />
                          <span>{member.email}</span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <select
                          value={member.role}
                          onChange={(e) => handleUpdateRole(member.userId, e.target.value)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium border-2 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors ${
                            member.role === 'admin'
                              ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-500 text-orange-700 dark:text-orange-400'
                              : 'bg-gray-50 dark:bg-[#2a2a2a] border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          <option value="member">Member</option>
                          <option value="admin">Admin</option>
                        </select>
                        <button
                          onClick={() => handleRemoveMember(member.userId)}
                          className="w-9 h-9 flex items-center justify-center rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                          title="Remove member"
                        >
                          <UserMinusIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-[#111111] rounded-b-2xl">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {members.length} member{members.length !== 1 ? 's' : ''} in this team
            </p>
            <div className="flex items-center space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-[#222222] rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {loading && (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                )}
                <span>Save Changes</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MembersModal;
